<template>
<div class="report-generator">
	<b-navbar class="submenu">
		<b-container>
			<b-navbar-brand href="#">SQL Report Generator</b-navbar-brand>
			
			<!-- Mobile-visible buttons -->
			<div class="d-md-none mobile-buttons">
				<b-button size="sm" variant="outline-primary" @click="executeQuery()" :disabled="!canExecute" class="mr-1">
					Execute
				</b-button>
				<b-button size="sm" variant="outline-success" 
					@click="saveQuery()" 
					:disabled="!canSave"
					v-if="selectedQuery || (editor && editor.getValue().trim().length > 0)"
					class="mr-1">
					Save
				</b-button>
				<b-button size="sm" variant="outline-secondary" @click="newQuery()" :disabled="!canNewQuery">
					New
				</b-button>
			</div>
			
			<!-- Desktop navigation -->
			<b-navbar-nav class="ml-auto d-none d-md-flex">
				<b-nav-item href="#" 
					@click="newQuery()" 
					:class="{ 'disabled': !canNewQuery }">
					New Query
				</b-nav-item>
				<b-nav-item href="#" 
					@click="saveQuery()" 
					v-if="selectedQuery || (editor && editor.getValue().trim().length > 0)"
					:class="{ 'disabled': !canSave }">
					Save Query
				</b-nav-item>
				<b-nav-item href="#" 
					@click="executeQuery()" 
					:class="{ 'disabled': !canExecute }">
					Execute Query
				</b-nav-item>
				<b-nav-item href="#" 
					@click="exportToCsv()" 
					:class="{ 'disabled': !canExport }"
					v-if="canExport">
					<b-spinner small type="grow" v-if="isExporting"></b-spinner>
					<span v-else>Export CSV</span>
				</b-nav-item>
			</b-navbar-nav>
		</b-container>
	</b-navbar>

	<b-alert
		v-model="showAlert"
		:variant="alertVariant"
		dismissible
		class="alert-fixed"
	>
		{{ alertMessage }}
	</b-alert>

	<div class="main-container" :class="{ 'results-expanded': isResultsExpanded }">
		<!-- Left panel with saved queries -->
		<div class="query-panel" v-show="!isResultsExpanded">
			<div class="query-panel-header">
				<h5>Saved Queries</h5>
			</div>
			<div class="query-list">
				<div v-for="q in queries" 
					:key="q.fileName" 
					class="query-item"
					:class="{ 'selected': selectedQuery === q.fileName }"
					@click="selectQuery(q)">
					<div class="query-content">
						<div class="query-name">{{ q.name }}</div>
						<!-- <div class="query-preview">{{ q.query.substring(0, 50) }}...</div> -->
					</div>
					<button 
						class="delete-button" 
						@click="initiateDeleteQuery(q, $event)"
						title="Delete query">
						×
					</button>
				</div>
			</div>
		</div>

		<!-- Right panel with editor and results -->
		<div class="content-panel" :class="{ 'expanded': isResultsExpanded }">
			<!-- Loading spinner -->
			<div v-if="isLoading" class="loading-overlay">
				<b-spinner variant="primary" type="grow"></b-spinner>
				<div class="mt-2">Executing query...</div>
			</div>

			<!-- Error message - only show for non-query execution errors -->
			<div v-if="queryError && !currentQueryExecuted" class="error-message">
				{{ queryError }}
			</div>

			<!-- Main content area -->
			<div class="content-wrapper">
				<!-- Editor container with resize handle -->
				<div class="editor-section" :style="{ height: editorHeight + 'px' }" v-show="!isResultsExpanded">
					<div class="editor-container" ref="editorContainer"></div>
					<div class="resize-handle" @mousedown="startResize">
						<div class="resize-handle-line"></div>
					</div>
				</div>

				<!-- Results area -->
				<div class="results-section" :class="{ 'full-page': isResultsExpanded }">
					<div v-if="currentQueryResults && currentQueryResults.length > 0" class="results-content">
						<div class="results-summary">
							<div class="results-count">
								<b-icon icon="table" class="mr-2"></b-icon>
								{{ resultsSummary }}
							</div>
							<div class="results-actions">
								<b-button
									size="sm"
									variant="outline-info"
									@click="toggleFullPageResults"
									:title="isResultsExpanded ? 'Exit full page' : 'Expand to full page'"
									class="mr-2"
								>
									<b-icon :icon="isResultsExpanded ? 'fullscreen-exit' : 'arrows-fullscreen'"></b-icon>
									{{ isResultsExpanded ? 'Exit' : 'Expand' }}
								</b-button>
								<div class="filter-box">
									<b-input-group size="sm">
										<b-input-group-prepend>
											<b-input-group-text>
												<b-icon icon="search"></b-icon>
											</b-input-group-text>
										</b-input-group-prepend>
										<b-form-input
											v-model="filterText"
											placeholder="Filter results..."
											debounce="300"
										></b-form-input>
										<b-input-group-append v-if="filterText">
											<b-button
												variant="outline-secondary"
												@click="filterText = ''"
												title="Clear filter"
											>
												<b-icon icon="x"></b-icon>
											</b-button>
										</b-input-group-append>
									</b-input-group>
								</div>
							</div>
						</div>
						<b-table
							:items="filteredResults"
							:fields="tableFields"
							striped
							hover
							responsive
							class="results-table"
							:sort-by.sync="sortBy"
							:sort-desc.sync="sortDesc"
							ref="resultsTable"
						>
							<template #cell()="data">
								<span 
									v-if="data.value === null" 
									class="text-muted">NULL</span>
								<span 
									v-else-if="typeof data.value === 'object'" 
									:title="JSON.stringify(data.value)">
									{{ data.value }}
								</span>
								<span 
									v-else 
									:title="data.value">
									{{ data.value }}
								</span>
							</template>
						</b-table>
					</div>
					<div v-else-if="queryError" class="no-results">
						<p class="text-danger">{{ queryError }}</p>
					</div>
					<div v-else-if="currentQueryExecuted" class="no-results">
						<p class="text-muted">Query executed successfully. No results returned.</p>
					</div>
					<div v-else class="no-results">
						<p class="text-muted">Execute a query to see results.</p>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- Modals -->
	<b-modal
		v-model="showSaveModal"
		title="Save Query"
		@ok="confirmSaveQuery"
		@cancel="cancelSaveQuery"
		ok-title="Save"
		cancel-title="Cancel"
	>
		<b-form-group label="Query Name" label-for="query-name">
			<b-form-input
				id="query-name"
				v-model="newQueryName"
				placeholder="Enter a name for this query"
				required
			></b-form-input>
		</b-form-group>
	</b-modal>

	<b-modal
		v-model="showUnsavedWarning"
		title="Unsaved Changes"
		@ok="confirmFileSwitch"
		@cancel="cancelQuerySwitch"
		ok-variant="danger"
		ok-title="Discard Changes"
		cancel-title="Stay on Current Query"
	>
		<p>You have unsaved changes. Are you sure you want to switch queries?</p>
		<p class="text-muted">Your changes will be lost if you proceed.</p>
	</b-modal>

	<!-- Add delete confirmation modal -->
	<b-modal
		v-model="showDeleteModal"
		title="Delete Query"
		@ok="confirmDeleteQuery"
		@cancel="queryToDelete = null"
		ok-variant="danger"
		ok-title="Delete"
		cancel-title="Cancel"
	>
		<p>Are you sure you want to delete the query "{{ queryToDelete && queryToDelete.name }}"?</p>
		<p class="text-muted">This action cannot be undone.</p>
	</b-modal>
</div>
</template>

<script>
import loader from '@monaco-editor/loader'

export default {
	name: 'ReportGenerator',
	data() {
		return {
			queries: [], // Will now store { fileName: string, name: string, query: string, lastModified: string }
			selectedQuery: null, // Will store the filename
			queryContent: '',
			isLoading: false,
			queryError: null,
			queryResults: null,
			queryResultsMap: {}, // New map to store results per query
			queryExecuted: false,
			queryExecutedMap: {}, // New map to store execution status per query
			showAlert: false,
			alertVariant: 'success',
			alertMessage: '',
			editor: null,
			monaco: null,
			editorInitialized: false,
			showUnsavedWarning: false,
			pendingQuerySwitch: null,
			hasUnsavedChanges: false,
			originalContent: '',
			showSaveModal: false,
			newQueryName: '',
			sortBy: '',
			sortDesc: false,
			editorHeight: 300,
			isDragging: false,
			dragStartY: 0,
			dragStartHeight: 0,
			isResultsExpanded: false,
			isExporting: false,
			showDeleteModal: false,
			queryToDelete: null,
			databaseSchema: null,
			schemaLoading: false,
			completionProvider: null,
			files: null, // Will store the list of files from S3
			filterText: '',
			rowsToShow: 100
		}
	},
	computed: {
		filteredQueries() {
			if (!this.files) return [];
			
			// Log the files we received to help debug
			console.log('Files from CMS/Configs:', this.files);
			
			return this.files
				.filter(f => {
					// Log each file being filtered
					console.log('Filtering file:', f);
					return f.fileName && f.fileName.toLowerCase().endsWith('.sql');
				})
				.map(f => {
					// Log each file being mapped
					console.log('Mapping file:', f);
					return {
						fileName: f.fileName,
						name: f.fileName.replace('.sql', '').replace(/-/g, ' '),
						query: f.query || '', // Will be loaded when selected
						lastModified: f.lastModified || f.LastModified || new Date().toISOString()
					};
				});
		},
		paginatedResults() {
			if (!this.queryResults) return [];
			return this.queryResults.slice(0, this.rowsToShow);
		},
		filteredResults() {
			if (!this.currentQueryResults) return [];
			if (!this.filterText) return this.currentQueryResults.slice(0, this.rowsToShow);

			const searchText = this.filterText.toLowerCase();
			return this.currentQueryResults.filter(row => {
				return Object.values(row).some(value => {
					if (value === null || value === undefined) return false;
					const strValue = String(value).toLowerCase();
					return strValue.includes(searchText);
				});
			}).slice(0, this.rowsToShow);
		},
		resultsSummary() {
			if (!this.currentQueryResults) return '';
			const totalRows = this.currentQueryResults.length;
			const filteredRows = this.filterText ? 
				this.currentQueryResults.filter(row => {
					const searchText = this.filterText.toLowerCase();
					return Object.values(row).some(value => {
						if (value === null || value === undefined) return false;
						const strValue = String(value).toLowerCase();
						return strValue.includes(searchText);
					});
				}).length : totalRows;
			const shownRows = Math.min(filteredRows, this.rowsToShow);
			return `Showing ${shownRows} of ${filteredRows} filtered rows (${totalRows} total)`;
		},
		canExecute() {
			return this.editor && this.editor.getValue().trim().length > 0;
		},
		canSave() {
			if (!this.editor) return false;
			const content = this.editor.getValue().trim();
			if (!content) return false;
			// Can save if it's a new query with content or an existing query with changes
			return !this.selectedQuery || (this.selectedQuery && this.hasUnsavedChanges);
		},
		canNewQuery() {
			// Can create new query if we have unsaved changes or if editor has content
			return this.hasUnsavedChanges || (this.editor && this.editor.getValue().trim().length > 0);
		},
		tableFields() {
			if (!this.currentQueryResults || this.currentQueryResults.length === 0) return [];
			
			return Object.keys(this.currentQueryResults[0]).map(key => ({
				key,
				label: key,
				sortable: true
			}));
		},
		canExport() {
			return this.currentQueryResults && this.currentQueryResults.length > 0;
		},
		currentQueryResults() {
			console.log('currentQueryResults computed:', {
				selectedQuery: this.selectedQuery,
				results: this.queryResultsMap[this.selectedQuery],
				allResults: this.queryResultsMap
			});
			if (!this.selectedQuery) return null;
			return this.queryResultsMap[this.selectedQuery] || null;
		},
		currentQueryExecuted() {
			console.log('currentQueryExecuted computed:', {
				selectedQuery: this.selectedQuery,
				executed: this.queryExecutedMap[this.selectedQuery],
				allExecuted: this.queryExecutedMap
			});
			if (!this.selectedQuery) return false;
			return this.queryExecutedMap[this.selectedQuery] || false;
		}
	},
	watch: {
		currentQueryResults() {
			this.makeTableColumnsResizable();
		}
	},
	methods: {
		toggleFullPageResults() {
			this.isResultsExpanded = !this.isResultsExpanded;
			
			// Trigger table resize after DOM update
			this.$nextTick(() => {
				if (this.$refs.resultsTable) {
					this.$refs.resultsTable.$forceUpdate();
				}
			});
		},
		
		async initMonaco() {
			if (this.monaco) return;

			try {
				// Configure loader with explicit version and paths
				loader.config({
					paths: {
						vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.36.1/min/vs'
					},
					'vs/nls': {
						availableLanguages: {
							'*': 'en'
						}
					}
				});

				// Initialize Monaco with explicit error handling
				this.monaco = await loader.init();
				console.log('Monaco initialized successfully');
				
				// Verify Monaco is properly loaded
				if (!this.monaco || !this.monaco.editor) {
					throw new Error('Monaco editor failed to initialize properly');
				}

				this.editorInitialized = true;
				return true;
			} catch (error) {
				console.error('Error initializing Monaco:', error);
				this.showMessage('Failed to initialize editor: ' + error.message, 'danger');
				return false;
			}
		},
		async initEditor() {
			try {
				// Ensure Monaco is initialized
				if (!this.monaco) {
					const initialized = await this.initMonaco();
					if (!initialized) {
						throw new Error('Failed to initialize Monaco editor');
					}
				}

				// Ensure container exists
				const container = this.$refs.editorContainer;
				if (!container) {
					console.error('Editor container not found, retrying in 100ms...');
					await new Promise(resolve => setTimeout(resolve, 100));
					const retryContainer = this.$refs.editorContainer;
					if (!retryContainer) {
						throw new Error('Editor container not found after retry');
					}
				}

				// Dispose existing editor if it exists
				if (this.editor) {
					this.editor.dispose();
					this.editor = null;
				}

				// Create new editor instance
				this.editor = this.monaco.editor.create(this.$refs.editorContainer, {
					value: this.queryContent,
					language: 'sql',
					theme: 'vs-dark',
					automaticLayout: true,
					minimap: { enabled: true },
					scrollBeyondLastLine: false,
					fontSize: 14,
					tabSize: 2,
					wordWrap: 'on',
					formatOnPaste: true,
					formatOnType: true,
					lineNumbers: 'on',
					glyphMargin: true,
					lineDecorationsWidth: 5,
					lineNumbersMinChars: 3,
					overviewRulerBorder: true,
					overviewRulerLanes: 2,
					hideCursorInOverviewRuler: false,
					renderLineHighlight: 'all',
					renderIndentGuides: true,
					renderWhitespace: 'all',
					folding: true,
					foldingStrategy: 'indentation',
					showFoldingControls: 'always'
				});

				// Handle content changes
				this.editor.onDidChangeModelContent(() => {
					const currentContent = this.editor.getValue();
					this.queryContent = currentContent;
					// Only set hasUnsavedChanges for existing queries
					if (this.selectedQuery) {
						this.hasUnsavedChanges = currentContent !== this.originalContent;
					}
				});

				// Force layout update
				this.editor.layout();
				console.log('Editor initialized successfully');

				// Only register completion provider if we have schema data
				if (this.databaseSchema) {
					this.registerCompletionProvider();
				}

				return true;
			} catch (error) {
				console.error('Error creating editor:', error);
				this.showMessage('Failed to create editor: ' + error.message, 'danger');
				return false;
			}
		},
		async loadQueries() {
			try {
				const response = await this.$http.get(`${this.$config.ApiUri}/CMS/Configs`);
				console.log('Raw response from CMS/Configs:', response.data);
				this.files = response.data;
				this.queries = this.filteredQueries;
				console.log('Processed queries:', this.queries);
			} catch (error) {
				console.error('Error loading queries:', error);
				this.showMessage('Error loading saved queries', 'danger');
			}
		},
		async selectQuery(query) {
			if (this.hasUnsavedChanges) {
				this.pendingQuerySwitch = query;
				this.showUnsavedWarning = true;
				return;
			}
			
			// If clicking the currently selected query, do nothing
			if (this.selectedQuery === query.fileName) return;
			
			await this.loadQuery(query);
		},
		async loadQuery(query) {
			console.log('loadQuery', query);
			this.selectedQuery = query.fileName;
			this.isLoading = true;
			this.queryError = null;
			
			try {
				console.log('Trying to load query:', query.fileName);
				const response = await this.$http.get(`${this.$config.ApiUri}/CMS/Configs/${query.fileName}`);
				console.log('Response from CMS/Configs:', response);
				if (response.data) {
					console.log('content', response.data.content);
					const content = response.data;
					console.log('Decoded content:', content);
					this.queryContent = content;
					this.originalContent = content;
					
					// Update the query in the list
					const index = this.queries.findIndex(q => q.fileName === query.fileName);
					if (index !== -1) {
						this.queries[index] = { ...this.queries[index], query: content };
					}
					
					// Initialize editor with the content
					await this.$nextTick();
					await this.initEditor();
				} else {
					throw new Error('No content found in file');
				}
			} catch (error) {
				console.error('Error loading query:', error);
				this.queryError = 'Error loading query: ' + error.message;
				this.showMessage(this.queryError, 'danger');
			} finally {
				this.isLoading = false;
				this.hasUnsavedChanges = false;
			}
		},
		async saveQuery() {
			if (!this.editor) return;
			
			const query = this.editor.getValue().trim();
			if (!query) {
				this.showMessage('Please enter an SQL query', 'warning');
				return;
			}

			// If it's an existing query (and not a temporary new query), save directly
			if (this.selectedQuery && !this.selectedQuery.startsWith('new_')) {
				try {
					const base64Content = btoa(query);
					
					// Use PUT like OfflinePolicySections
					await this.$http.put(`${this.$config.ApiUri}/CMS/Configs/${this.selectedQuery}`, 
						`"${base64Content}"`,
						{
							headers: {
								'Content-Type': 'application/json'
							},
							transformRequest: [(data) => data]
						}
					);
					
					// Update the query in the list
					const index = this.queries.findIndex(q => q.fileName === this.selectedQuery);
					if (index !== -1) {
						this.queries[index] = { 
							...this.queries[index], 
							query: query,
							lastModified: new Date().toISOString()
						};
					}
					
					this.originalContent = query;
					this.hasUnsavedChanges = false;
					this.showMessage('Query saved successfully');
					
					// Refresh the file list
					await this.loadQueries();
				} catch (error) {
					console.error('Error saving query:', error);
					this.showMessage('Error saving query', 'danger');
				}
			} else {
				// For new queries, show the save modal
				this.newQueryName = '';
				this.showSaveModal = true;
			}
		},
		async confirmSaveQuery() {
			if (!this.newQueryName.trim()) {
				this.showMessage('Please enter a query name', 'warning');
				return;
			}

			try {
				const query = this.editor.getValue().trim();
				if (!query) {
					this.showMessage('Please enter an SQL query', 'warning');
					return;
				}

				// Sanitize the filename
				const sanitizedName = this.newQueryName.trim()
					.toLowerCase()
					.replace(/[^a-z0-9-]/g, '-')
					.replace(/-+/g, '-')
					.replace(/^-|-$/g, '');

				const fileName = `${sanitizedName}.sql`;
				const base64Content = btoa(query);

				// Use PUT like OfflinePolicySections
				await this.$http.put(`${this.$config.ApiUri}/CMS/Configs/${fileName}`, 
					`"${base64Content}"`,
					{
						headers: {
							'Content-Type': 'application/json'
						},
						transformRequest: [(data) => data]
					}
				);

				// Add to the list
				const newQuery = {
					fileName: fileName,
					name: this.newQueryName.trim(),
					query: query,
					lastModified: new Date().toISOString()
				};
				this.queries.push(newQuery);
				this.selectedQuery = fileName;

				this.originalContent = query;
				this.hasUnsavedChanges = false;
				this.showSaveModal = false;
				this.showMessage('Query saved successfully');
				
				// Refresh the file list
				await this.loadQueries();
			} catch (error) {
				console.error('Error saving query:', error);
				this.showMessage('Error saving query', 'danger');
			}
		},
		cancelSaveQuery() {
			this.showSaveModal = false;
			this.newQueryName = '';
			
			// If this was a new query and user cancels, reset to default state
			if (!this.selectedQuery) {
				this.queryContent = 'SELECT * FROM your_table LIMIT 100;';
				this.originalContent = this.queryContent;
				if (this.editor) {
					this.editor.setValue(this.queryContent);
				}
			}
		},
		validateSqlQuery(query) {
			// Convert to uppercase and remove comments for validation
			const cleanQuery = query
				.replace(/\/\*[\s\S]*?\*\/|--.*$/gm, '') // Remove SQL comments
				.trim()
				.toUpperCase();

			// Check if query starts with SELECT
			if (!cleanQuery.startsWith('SELECT')) {
				return {
					valid: false,
					message: 'Only SELECT queries are allowed'
				};
			}

			// Check for dangerous keywords
			const dangerousKeywords = [
				'INSERT', 'UPDATE', 'DELETE', 'DROP', 'TRUNCATE', 'ALTER', 
				'CREATE', 'REPLACE', 'MERGE', 'EXEC', 'EXECUTE', 'DECLARE',
				'GRANT', 'REVOKE', 'DENY', 'BACKUP', 'RESTORE'
			];

			for (const keyword of dangerousKeywords) {
				// Use word boundary to avoid matching parts of other words
				const regex = new RegExp(`\\b${keyword}\\b`, 'i');
				if (regex.test(cleanQuery)) {
					return {
						valid: false,
						message: `Query contains forbidden keyword: ${keyword}`
					};
				}
			}

			// Check for semicolons that might indicate multiple statements
			if (cleanQuery.includes(';')) {
				const statements = cleanQuery.split(';').map(s => s.trim()).filter(s => s);
				if (statements.length > 1) {
					return {
						valid: false,
						message: 'Multiple statements are not allowed'
					};
				}
			}

			return { valid: true };
		},
		async executeQuery() {
			if (!this.editor) return;
			
			const query = this.editor.getValue().trim();
			if (!query) {
				this.showMessage('Please enter an SQL query', 'warning');
				return;
			}

			// Validate the query
			const validation = this.validateSqlQuery(query);
			if (!validation.valid) {
				this.showMessage(validation.message, 'danger');
				return;
			}

			this.isLoading = true;
			this.queryError = null; // Clear any previous errors

			try {
				var singleLineQuery = query.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
				const response = await this.$http.post(`${this.$config.ApiUri}/Discounts/SQLReports/Execute`, 
					{ sql: singleLineQuery },
					{
						headers: {
							'Content-Type': 'application/json'
						}
					}
				);
				
				// Check if the response contains an error
				if (response.data && response.data.error) {
					// For new queries, create a temporary key
					if (!this.selectedQuery) {
						const tempKey = `new_${Date.now()}`;
						this.selectedQuery = tempKey;
					}
					
					// Clear any previous results and set error state
					this.$set(this.queryResultsMap, this.selectedQuery, null);
					this.$set(this.queryExecutedMap, this.selectedQuery, true);
					this.queryError = response.data.error; // This will be used for display in results area
					return;
				}
				
				// For new queries, create a temporary key
				if (!this.selectedQuery) {
					const tempKey = `new_${Date.now()}`;
					this.selectedQuery = tempKey;
				}

				// Store results in the map using the query name
				this.$set(this.queryResultsMap, this.selectedQuery, response.data);
				this.$set(this.queryExecutedMap, this.selectedQuery, true);
				
				// Force a reactive update
				this.$forceUpdate();
				
				this.showMessage('Query executed successfully');
			} catch (error) {
				this.queryError = 'Error executing query';
				this.showMessage(this.queryError, 'danger');
			} finally {
				this.isLoading = false;
			}
		},
		showMessage(message, variant = 'success') {
			this.alertMessage = message;
			this.alertVariant = variant;
			this.showAlert = true;
			if (variant === 'success') {
				setTimeout(() => {
					this.showAlert = false;
				}, 5000);
			}
		},
		async confirmFileSwitch() {
			this.showUnsavedWarning = false;
			if (this.pendingQuerySwitch) {
				if (this.pendingQuerySwitch.fileName) {
					await this.loadQuery(this.pendingQuerySwitch);
				} else {
					await this.loadQuery({ fileName: null, query: '' });
				}
				this.pendingQuerySwitch = null;
			}
		},
		cancelQuerySwitch() {
			this.showUnsavedWarning = false;
			this.pendingQuerySwitch = null;
		},
		startResize(event) {
			this.isDragging = true;
			this.dragStartY = event.clientY;
			this.dragStartHeight = this.editorHeight;
			document.addEventListener('mousemove', this.handleResize);
			document.addEventListener('mouseup', this.stopResize);
			event.preventDefault();
		},
		handleResize(event) {
			if (!this.isDragging) return;
			
			const deltaY = event.clientY - this.dragStartY;
			const newHeight = Math.max(200, this.dragStartHeight + deltaY); // Minimum height of 200px
			this.editorHeight = newHeight;
			
			// Update editor layout
			if (this.editor) {
				this.editor.layout();
			}
		},
		stopResize() {
			this.isDragging = false;
			document.removeEventListener('mousemove', this.handleResize);
			document.removeEventListener('mouseup', this.stopResize);
		},
		exportToCsv() {
			if (!this.currentQueryResults || this.currentQueryResults.length === 0) {
				this.showMessage('No data to export', 'warning');
				return;
			}

			this.isExporting = true;
			try {
				// Get headers from the first row
				const headers = Object.keys(this.currentQueryResults[0]);
				
				// Convert data to CSV format
				const csvContent = [
					// Headers row
					headers.join(','),
					// Data rows
					...this.currentQueryResults.map(row => 
						headers.map(header => {
							const value = row[header];
							// Handle different data types and escape special characters
							if (value === null || value === undefined) return '';
							if (typeof value === 'object') return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
							if (typeof value === 'string') return `"${value.replace(/"/g, '""')}"`;
							return value;
						}).join(',')
					)
				].join('\n');

				// Create blob and download
				const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
				const link = document.createElement('a');
				const url = URL.createObjectURL(blob);
				
				// Generate filename based on query name or timestamp
				const filename = this.selectedQuery 
					? `query_${this.queries.find(q => q.fileName === this.selectedQuery)?.name || 'export'}`
					: `query_export_${new Date().toISOString().slice(0,19).replace(/[:]/g, '-')}`;
				
				link.setAttribute('href', url);
				link.setAttribute('download', `${filename}.csv`);
				link.style.visibility = 'hidden';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				
				this.showMessage('CSV export completed successfully');
			} catch (error) {
				console.error('Error exporting to CSV:', error);
				this.showMessage('Error exporting to CSV: ' + error.message, 'danger');
			} finally {
				this.isExporting = false;
			}
		},
		async confirmDeleteQuery() {
			if (!this.queryToDelete) return;
			
			try {
				// Delete from CMS/Configs
				await this.$http.delete(`${this.$config.ApiUri}/CMS/Configs/${this.queryToDelete.fileName}`);

				// Remove from the list
				const index = this.queries.findIndex(q => q.fileName === this.queryToDelete.fileName);
				if (index !== -1) {
					this.queries.splice(index, 1);
				}

				// If the deleted query was selected, clear the editor
				if (this.selectedQuery === this.queryToDelete.fileName) {
					this.selectedQuery = null;
					this.queryContent = '';
					this.originalContent = '';
					this.hasUnsavedChanges = false;
					if (this.editor) {
						this.editor.setValue('');
					}
				}
				
				this.showMessage('Query deleted successfully');
				
				// Refresh the file list
				await this.loadQueries();
			} catch (error) {
				console.error('Error deleting query:', error);
				this.showMessage('Error deleting query', 'danger');
			} finally {
				this.showDeleteModal = false;
				this.queryToDelete = null;
			}
		},
		initiateDeleteQuery(query, event) {
			event.stopPropagation(); // Prevent query selection
			this.queryToDelete = query;
			this.showDeleteModal = true;
		},
		async loadDatabaseSchema() {
			if (this.schemaLoading) return;
			this.schemaLoading = true;
			
			try {
				const response = await this.$http.get(`${this.$config.ApiUri}/Discounts/SQLReports/SchemaDescription`);
				console.log('Raw database schema response:', response.data);
				
				// Transform the schema response into the expected format
				const schemaData = response.data;
				if (!Array.isArray(schemaData)) {
					throw new Error('Invalid schema response: expected an array');
				}

				// Extract unique schemas
				const schemas = [...new Set(schemaData.map(item => item.TABLE_SCHEMA))];
				
				// Group tables by schema
				const tables = {};
				schemas.forEach(schema => {
					tables[schema] = [];
					
					// Get all tables for this schema
					const schemaTables = schemaData
						.filter(item => item.TABLE_SCHEMA === schema)
						.reduce((acc, item) => {
							if (!acc[item.TABLE_NAME]) {
								acc[item.TABLE_NAME] = {
									name: item.TABLE_NAME,
									columns: []
								};
							}
							acc[item.TABLE_NAME].columns.push({
								name: item.COLUMN_NAME,
								type: item.DATA_TYPE,
								description: '' // Add description if available in the schema
							});
							return acc;
						}, {});
					
					// Convert to array and add to tables
					tables[schema] = Object.values(schemaTables);
				});

				this.databaseSchema = {
					schemas,
					tables
				};
				
				console.log('Transformed database schema:', this.databaseSchema);
				
				// Register completion provider after schema is loaded
				this.registerCompletionProvider();
			} catch (error) {
				console.error('Error loading database schema:', error);
				this.showMessage('Error loading database schema: ' + error.message, 'danger');
			} finally {
				this.schemaLoading = false;
			}
		},
		getSqlKeywords() {
			return [
				{ label: 'SELECT', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'SELECT', detail: 'SQL Keyword' },
				{ label: 'FROM', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'FROM', detail: 'SQL Keyword' },
				{ label: 'WHERE', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'WHERE', detail: 'SQL Keyword' },
				{ label: 'GROUP BY', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'GROUP BY', detail: 'SQL Keyword' },
				{ label: 'ORDER BY', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'ORDER BY', detail: 'SQL Keyword' },
				{ label: 'HAVING', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'HAVING', detail: 'SQL Keyword' },
				{ label: 'JOIN', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'JOIN', detail: 'SQL Keyword' },
				{ label: 'LEFT JOIN', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'LEFT JOIN', detail: 'SQL Keyword' },
				{ label: 'RIGHT JOIN', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'RIGHT JOIN', detail: 'SQL Keyword' },
				{ label: 'INNER JOIN', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'INNER JOIN', detail: 'SQL Keyword' },
				{ label: 'ON', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'ON', detail: 'SQL Keyword' },
				{ label: 'AS', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'AS', detail: 'SQL Keyword' },
				{ label: 'DISTINCT', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'DISTINCT', detail: 'SQL Keyword' },
				{ label: 'LIMIT', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'LIMIT', detail: 'SQL Keyword' },
				{ label: 'OFFSET', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'OFFSET', detail: 'SQL Keyword' },
				{ label: 'AND', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'AND', detail: 'SQL Keyword' },
				{ label: 'OR', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'OR', detail: 'SQL Keyword' },
				{ label: 'NOT', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'NOT', detail: 'SQL Keyword' },
				{ label: 'IN', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'IN', detail: 'SQL Keyword' },
				{ label: 'LIKE', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'LIKE', detail: 'SQL Keyword' },
				{ label: 'IS NULL', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'IS NULL', detail: 'SQL Keyword' },
				{ label: 'IS NOT NULL', kind: this.monaco.languages.CompletionItemKind.Keyword, insertText: 'IS NOT NULL', detail: 'SQL Keyword' },
				{ label: 'COUNT', kind: this.monaco.languages.CompletionItemKind.Function, insertText: 'COUNT(${1:expression})', insertTextRules: this.monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: 'Aggregate Function' },
				{ label: 'SUM', kind: this.monaco.languages.CompletionItemKind.Function, insertText: 'SUM(${1:expression})', insertTextRules: this.monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: 'Aggregate Function' },
				{ label: 'AVG', kind: this.monaco.languages.CompletionItemKind.Function, insertText: 'AVG(${1:expression})', insertTextRules: this.monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: 'Aggregate Function' },
				{ label: 'MAX', kind: this.monaco.languages.CompletionItemKind.Function, insertText: 'MAX(${1:expression})', insertTextRules: this.monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: 'Aggregate Function' },
				{ label: 'MIN', kind: this.monaco.languages.CompletionItemKind.Function, insertText: 'MIN(${1:expression})', insertTextRules: this.monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: 'Aggregate Function' }
			];
		},
		registerCompletionProvider() {
			if (!this.monaco || !this.databaseSchema) return;

			// Dispose existing provider if any
			if (this.completionProvider) {
				this.completionProvider.dispose();
			}

			// Get SQL keywords
			const keywordItems = this.getSqlKeywords();

			// Create completion items for schemas
			const schemaItems = this.databaseSchema.schemas.map(schema => ({
				label: schema,
				kind: this.monaco.languages.CompletionItemKind.Module,
				detail: 'Schema',
				documentation: `Schema: ${schema}`,
				insertText: schema,
				sortText: '0' + schema // Ensure schemas appear first
			}));

			// Create completion items for tables
			const tableItems = Object.entries(this.databaseSchema.tables).flatMap(([schema, tables]) =>
				tables.map(table => ({
					label: table.name.toLowerCase(), // Normalize to lowercase
					kind: this.monaco.languages.CompletionItemKind.Class,
					detail: `Table in ${schema}`,
					documentation: {
						value: [
							`**Table**: ${table.name}`,
							`**Schema**: ${schema}`,
							'',
							'**Columns**:',
							...table.columns.map(col => `- ${col.name} (${col.type}): ${col.description}`)
						].join('\n')
					},
					insertText: table.name, // Keep original case for insert
					sortText: '1' + table.name.toLowerCase() // Sort by lowercase
				}))
			);

			// Create completion items for columns
			const columnItems = Object.entries(this.databaseSchema.tables).flatMap(([schema, tables]) =>
				tables.flatMap(table =>
					table.columns.map(column => ({
						label: column.name.toLowerCase(), // Normalize to lowercase
						kind: this.monaco.languages.CompletionItemKind.Field,
						detail: `${column.type} in ${table.name}`,
						documentation: {
							value: [
								`**Column**: ${column.name}`,
								`**Type**: ${column.type}`,
								`**Table**: ${table.name}`,
								`**Schema**: ${schema}`,
								'',
								column.description
							].join('\n')
						},
						insertText: column.name, // Keep original case for insert
						sortText: '2' + column.name.toLowerCase() // Sort by lowercase
					}))
				)
			);

			// Register the completion provider
			this.completionProvider = this.monaco.languages.registerCompletionItemProvider('sql', {
				triggerCharacters: ['.', ' ', '*'],
				provideCompletionItems: (model, position) => {
					// Get the entire query content up to the current position
					const queryContent = model.getValueInRange({
						startLineNumber: 1,
						startColumn: 1,
						endLineNumber: position.lineNumber,
						endColumn: position.column
					});
					
					// Get the current word being typed
					const wordInfo = model.getWordUntilPosition(position);
					const currentWord = (wordInfo.word || '').toLowerCase();
					
					// Check if we're after FROM
					const afterFrom = queryContent.match(/\bFROM\s+$/i);
					
					// Get all possible suggestions
					let suggestions = [];
					
					if (afterFrom) {
						// After FROM, only show tables
						suggestions = tableItems.map(item => ({
							label: item.label,
							kind: this.monaco.languages.CompletionItemKind.Class,
							detail: item.detail,
							documentation: item.documentation,
							insertText: item.insertText,
							range: {
								startLineNumber: position.lineNumber,
								startColumn: position.column - currentWord.length,
								endLineNumber: position.lineNumber,
								endColumn: position.column
							}
						}));
					} else {
						// Otherwise show everything
						suggestions = [
							...schemaItems,
							...tableItems,
							...columnItems,
							...keywordItems
						].map(item => ({
							...item,
							range: {
								startLineNumber: position.lineNumber,
								startColumn: position.column - currentWord.length,
								endLineNumber: position.lineNumber,
								endColumn: position.column
							}
						}));
					}

					// If we're typing something, filter to only show suggestions that start with it
					if (currentWord) {
						suggestions = suggestions.filter(s => 
							s.label.toLowerCase().startsWith(currentWord.toLowerCase()),
						);
					}

					// Log the final suggestions
					console.log('Final suggestions:', suggestions.map(s => ({
						label: s.label,
						insertText: s.insertText,
						kind: s.kind,
						range: s.range
					})));

					return {
						suggestions: suggestions,
						incomplete: false
					};
				}
			});
		},
		newQuery() {
			// If there are unsaved changes, show warning
			if (this.hasUnsavedChanges) {
				this.pendingQuerySwitch = { fileName: null, query: '' };
				this.showUnsavedWarning = true;
				return;
			}

			// Clear the editor and reset state
			if (this.editor) {
				this.editor.setValue('');
			}
			this.selectedQuery = null;
			this.queryContent = '';
			this.originalContent = '';
			this.hasUnsavedChanges = false;
			this.queryError = null;
			// Don't clear results - they're now stored per query
		},
		formatValue(value) {
			if (value === null || value === undefined) return 'NULL';
			if (typeof value === 'object') {
				const str = JSON.stringify(value);
				return str.length > 50 ? str.substring(0, 47) + '...' : str;
			}
			const str = String(value);
			return str.length > 50 ? str.substring(0, 47) + '...' : str;
		},
		makeTableColumnsResizable() {
			this.$nextTick(() => {
				const table = this.$refs.resultsTable?.$el?.querySelector('table');
				if (!table) return;

				const headers = table.querySelectorAll('th');
				headers.forEach((header) => {
					// Remove existing resize handle if any
					const existingHandle = header.querySelector('.column-resize-handle');
					if (existingHandle) {
						existingHandle.remove();
					}

					// Create resize handle
					const resizeHandle = document.createElement('div');
					resizeHandle.className = 'column-resize-handle';
					resizeHandle.style.cssText = `
						position: absolute;
						top: 0;
						right: 0;
						width: 10px;
						height: 100%;
						cursor: col-resize;
						background: rgba(0, 123, 255, 0.2);
						border-right: 2px solid rgba(0, 123, 255, 0.4);
						z-index: 10;
					`;

					// Add hover effect
					resizeHandle.addEventListener('mouseenter', () => {
						resizeHandle.style.background = 'rgba(0, 123, 255, 0.4)';
						resizeHandle.style.borderRight = '2px solid rgba(0, 123, 255, 0.8)';
					});
					resizeHandle.addEventListener('mouseleave', () => {
						resizeHandle.style.background = 'rgba(0, 123, 255, 0.2)';
						resizeHandle.style.borderRight = '2px solid rgba(0, 123, 255, 0.4)';
					});

					// Add resize functionality
					resizeHandle.addEventListener('mousedown', (e) => {
						e.preventDefault();
						e.stopPropagation(); // Prevent sorting when clicking resize handle
						e.stopImmediatePropagation(); // Stop all other event handlers
						const startX = e.clientX;
						const startWidth = header.offsetWidth;

						const handleMouseMove = (e) => {
							const newWidth = Math.max(50, startWidth + (e.clientX - startX));
							header.style.width = newWidth + 'px';
							header.style.minWidth = newWidth + 'px';
							
							// Also set width for corresponding cells in the body
							const columnIndex = Array.from(header.parentElement.children).indexOf(header);
							const rows = table.querySelectorAll('tbody tr');
							rows.forEach(row => {
								const cell = row.children[columnIndex];
								if (cell) {
									cell.style.width = newWidth + 'px';
									cell.style.minWidth = newWidth + 'px';
								}
							});
						};

						const handleMouseUp = () => {
							document.removeEventListener('mousemove', handleMouseMove);
							document.removeEventListener('mouseup', handleMouseUp);
						};

						document.addEventListener('mousemove', handleMouseMove);
						document.addEventListener('mouseup', handleMouseUp);
					});

					// Also prevent click events from bubbling up
					resizeHandle.addEventListener('click', (e) => {
						e.preventDefault();
						e.stopPropagation();
						e.stopImmediatePropagation();
					});

					// Make header position relative so the handle can be positioned absolutely
					header.style.position = 'relative';
					header.appendChild(resizeHandle);
				});
			});
		}
	},
	async mounted() {
		try {
			console.log('Component mounting...');
			
			// Initialize Monaco first
			const monacoInitialized = await this.initMonaco();
			if (!monacoInitialized) {
				throw new Error('Failed to initialize Monaco');
			}
			console.log('Monaco initialized');

			// Load database schema once during mount
			await this.loadDatabaseSchema();
			console.log('Database schema loaded');

			// Load queries from S3
			await this.loadQueries();
			console.log('Queries loaded from S3');

			// Wait for DOM to be ready
			await this.$nextTick();
			console.log('DOM updated');

			// Initialize editor with retry logic
			let retryCount = 0;
			const maxRetries = 3;
			
			while (retryCount < maxRetries) {
				try {
					const editorInitialized = await this.initEditor();
					if (editorInitialized) {
						console.log('Editor initialized successfully on attempt', retryCount + 1);
						break;
					}
				} catch (error) {
					console.warn(`Editor initialization attempt ${retryCount + 1} failed:`, error);
					retryCount++;
					if (retryCount < maxRetries) {
						await new Promise(resolve => setTimeout(resolve, 200 * retryCount));
					} else {
						throw new Error('Failed to initialize editor after multiple attempts');
					}
				}
			}
		} catch (error) {
			console.error('Error during component mount:', error);
			this.showMessage('Error initializing editor: ' + error.message, 'danger');
		}
	},
	beforeDestroy() {
		// Clean up event listeners
		document.removeEventListener('mousemove', this.handleResize);
		document.removeEventListener('mouseup', this.stopResize);

		// Clean up completion provider
		if (this.completionProvider) {
			this.completionProvider.dispose();
		}
	}
}
</script>

<style scoped>
.report-generator {
	display: flex;
	flex-direction: column;
	height: 100vh;
	background-color: #f8f9fa;
}

.main-container {
	display: flex;
	flex: 1;
	overflow: hidden;
	height: calc(100vh - 56px);
}

.query-panel {
	width: 300px;
	display: flex;
	flex-direction: column;
	background-color: white;
	border-right: 1px solid #dee2e6;
}

.query-panel-header {
	padding: 16px;
	border-bottom: 1px solid #dee2e6;
	display: flex;
	justify-content: space-between;
	align-items: center;
	background-color: white;
}

.query-panel-header h5 {
	margin: 0;
	flex: 1;
}

.query-list {
	flex: 1;
	overflow-y: auto;
}

.content-panel {
	flex: 1;
	display: flex;
	flex-direction: column;
	background-color: white;
	position: relative;
	overflow: hidden;
}

.content-wrapper {
	display: flex;
	flex-direction: column;
	height: 100%;
	padding: 20px;
	gap: 20px;
	overflow: hidden;
}

.query-item {
	padding: 12px 16px;
	border-bottom: 1px solid #dee2e6;
	cursor: pointer;
	background-color: white;
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 8px;
}

.query-item:hover {
	background-color: #f8f9fa;
}

.query-item.selected {
	background-color: #e3f2fd;
	border-left: 4px solid #1976d2;
}

.query-content {
	flex: 1;
	min-width: 0; /* Allows text truncation to work */
}

.query-name {
	font-weight: 500;
	margin-bottom: 4px;
}

.query-preview {
	font-size: 0.875rem;
	color: #666;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.delete-button {
	background: none;
	border: none;
	color: #dc3545;
	padding: 2px 8px;
	cursor: pointer;
	opacity: 0.5;
	transition: all 0.2s;
	border-radius: 4px;
	font-size: 20px;
	line-height: 1;
	min-width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.delete-button:hover {
	background-color: #ffebee;
	opacity: 1;
}

.query-item:hover .delete-button {
	opacity: 0.7;
}

.query-item.selected .delete-button {
	opacity: 0.5;
}

.query-item.selected:hover .delete-button {
	opacity: 0.7;
}

.editor-section {
	flex: 0 0 auto;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	overflow: hidden;
	background-color: #1e1e1e;
	position: relative;
	display: flex;
	flex-direction: column;
	min-height: 200px;
	max-height: calc(100vh - 300px);
}

.editor-container {
	flex: 1;
	width: 100%;
	min-height: 200px;
}

.resize-handle {
	height: 8px;
	background-color: #f8f9fa;
	cursor: ns-resize;
	display: flex;
	align-items: center;
	justify-content: center;
	border-top: 1px solid #dee2e6;
	user-select: none;
}

.resize-handle:hover {
	background-color: #e9ecef;
}

.resize-handle-line {
	width: 40px;
	height: 2px;
	background-color: #adb5bd;
	border-radius: 1px;
}

.resize-handle:active {
	background-color: #dee2e6;
}

.results-section {
	flex: 1;
	overflow: auto;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	background-color: white;
	min-height: 100px;
	display: flex;
	flex-direction: column;
}

.results-content {
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
}

.results-table {
	margin: 0;
	flex: 1;
}

.loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	background-color: rgba(255, 255, 255, 0.9);
	z-index: 1000;
}

.error-message {
	margin: 20px;
	padding: 16px;
	color: #d32f2f;
	background-color: #ffebee;
	border-radius: 4px;
}

.no-results {
	text-align: center;
	padding: 20px;
	color: #666;
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
}

.alert-fixed {
	position: fixed;
	top: 56px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 1000;
	min-width: 300px;
	text-align: center;
}

.submenu {
	background-color: #f8f9fa;
	border-bottom: 1px solid #dee2e6;
}

.submenu a {
	color: #495057;
	text-decoration: none;
	padding: 0.5rem 1rem;
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
}

.submenu a:hover {
	color: #007bff;
}

.submenu a.disabled {
	color: #6c757d;
	cursor: not-allowed;
	opacity: 0.65;
}

.truncated-cell {
	display: inline-block;
	max-width: 300px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	vertical-align: middle;
}

.results-table td {
	max-width: 200px;
	padding: 0.5rem;
}

.results-table {
	font-size: 0.9rem;
}

.results-summary {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.75rem 1rem;
	background-color: #f8f9fa;
	border-bottom: 1px solid #dee2e6;
	gap: 1rem;
}

.results-actions {
	display: flex;
	align-items: center;
	gap: 10px;
}

.results-count {
	font-size: 0.95rem;
	color: #495057;
	font-weight: 500;
	display: flex;
	align-items: center;
}

.results-count .b-icon {
	color: #6c757d;
}

.filter-box {
	min-width: 250px;
	max-width: 400px;
}

.filter-box .input-group {
	background-color: white;
	border-radius: 4px;
	box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.filter-box .form-control {
	border: 1px solid #ced4da;
}

.filter-box .input-group-text {
	background-color: white;
	border-right: none;
}

.filter-box .form-control:focus {
	box-shadow: none;
	border-color: #80bdff;
}

.filter-box .btn-outline-secondary {
	border-color: #ced4da;
	border-left: none;
}

.filter-box .btn-outline-secondary:hover {
	background-color: #f8f9fa;
	border-color: #ced4da;
}

/* Column resizing styles */
.results-table {
	table-layout: fixed;
}

.results-table th {
	border-right: 1px solid #dee2e6;
}

.results-table td {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

/* Mobile-only responsive design - preserves desktop layout */
@media (max-width: 768px) {
	/* Mobile navbar improvements */
	.submenu .navbar-brand {
		font-size: 14px;
		flex: 1;
	}
	
	.mobile-buttons {
		display: flex;
		gap: 4px;
		align-items: center;
	}
	
	.mobile-buttons .btn {
		font-size: 11px;
		padding: 4px 8px;
		white-space: nowrap;
	}
	
	.navbar-nav .nav-item {
		padding: 2px 0;
	}
	
	.navbar-nav .nav-link {
		padding: 8px 12px;
		font-size: 14px;
		border-radius: 4px;
		margin: 2px 0;
	}
	
	.navbar-nav .nav-link:hover {
		background-color: rgba(0,0,0,0.05);
	}
	
	.navbar-nav .nav-link.disabled {
		opacity: 0.5;
	}
	
	.main-container {
		flex-direction: column;
		height: auto;
		min-height: calc(100vh - 56px);
		padding: 10px;
		gap: 10px;
	}
	
	/* Query panel becomes horizontal list at top */
	.query-panel {
		width: 100%;
		max-height: 150px;
		order: 1;
		border-right: none;
		border: 1px solid #dee2e6;
		border-radius: 5px;
	}
	
	.query-list {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		padding: 10px;
	}
	
	.query-item {
		border: 1px solid #dee2e6;
		border-radius: 4px;
		padding: 8px 12px;
		margin: 0;
		background: white;
		min-width: auto;
		max-width: none;
		font-size: 13px;
	}
	
	.query-item.selected {
		border-left: 4px solid #1976d2;
		background-color: #e3f2fd;
	}
	
	/* Content panel stacks below */
	.content-panel {
		order: 2;
		min-height: 400px;
	}
	
	.content-wrapper {
		padding: 10px;
		gap: 10px;
	}
	
	/* Editor section */
	.editor-section {
		min-height: 200px;
		max-height: 300px;
		border: 1px solid #dee2e6;
		border-radius: 5px;
	}
	
	/* Results section */
	.results-section {
		min-height: 200px;
		border: 1px solid #dee2e6;
		border-radius: 5px;
		padding: 10px;
	}
	
	/* Mobile table improvements */
	.results-table {
		font-size: 12px;
	}
	
	.results-table th,
	.results-table td {
		padding: 4px 6px;
		font-size: 12px;
	}
}

/* Full page results mode */
.main-container.results-expanded {
	position: fixed;
	top: 56px;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 1000;
	background: white;
	padding: 10px;
}

.content-panel.expanded {
	width: 100%;
	max-width: none;
}

.results-section.full-page {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: white;
	border: none;
	border-radius: 0;
	padding: 10px;
	overflow: auto;
}

.results-section.full-page .results-table {
	max-height: calc(100vh - 200px);
	height: calc(100vh - 200px);
}
</style>